import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Output, input } from '@angular/core';
import { FormField } from '@angular/forms/signals';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';

import { ESocialNetworks } from '@common/enums';
import { isEmptyButtonLabel } from '@common/helpers/forms';

import type { SocialItem } from '@sites/entity-core';
import type { SocialForm } from '@sites/entity-signal-form';

import { ICON_NAMES, SocialNetworksNames } from '@web/widgets/social/constants/social-widget.const';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

/**
 * Per-social editor — fully signal-based. Receives the shared widget form
 * plus the index of the entry being edited; writes go through
 * `form.updateSocial(index, patch)` (or a `[formField]` binding on the item's
 * FieldTree leaves), so the preview updates live.
 *
 * Save/cancel semantics are owned by the parent control: it opens a form
 * transaction before mounting this component, commits on `saveEvent` and
 * rolls back on `cancelEvent` — replacing the FormGroup era's
 * cached-initial-value reset in `WebBaseControlTypedSettingsComponent`.
 */
@Component({
    selector: 'web-social-settings',
    templateUrl: './social-settings.component.html',
    styleUrls: ['./social-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, BsDropdownModule, FormField],
})
export class SocialSettingsComponent {
    public readonly form = input.required<SocialForm>();
    public readonly index = input.required<number>();

    @Output() public saveEvent: EventEmitter<void> = new EventEmitter();
    @Output() public cancelEvent: EventEmitter<void> = new EventEmitter();

    public readonly ICON_NAMES = ICON_NAMES;
    public readonly socialNetworksNames = SocialNetworksNames;
    public readonly ESocialNetworks = ESocialNetworks;

    /** The name doubles as the icon's title — blank leaves the link unlabelled. */
    public get saveDisablePredicate(): boolean {
        return isEmptyButtonLabel(this.form().socialAt(this.index())?.name().value());
    }

    private patch(patch: Partial<SocialItem>): void {
        this.form().updateSocial(this.index(), patch);
    }

    public setIconName(name: string): void {
        this.patch({
            socialNetwork: name as SocialItem['socialNetwork'],
            name: this.socialNetworksNames[ESocialNetworks[name]],
        });
    }

    public togglePreventLinkConverting(value: boolean): void {
        this.patch({ preventLinkConverting: value });
    }

    public getSocialNetworkName(social: string): string {
        return this.socialNetworksNames[ESocialNetworks[social]];
    }

    public getPlaceholder(social: SocialItem['socialNetwork']): string {
        switch (social) {
            case ESocialNetworks.email:
                return 'example@mail.com';
            case ESocialNetworks.phone:
                return '+1 234 567 89 10';
            default:
                return 'https://site.com';
        }
    }

    // --- Save / cancel ---

    public onSave(): void {
        if (this.saveDisablePredicate) {
            return;
        }

        this.saveEvent.emit();
    }

    public onReset(): void {
        this.cancelEvent.emit();
    }

    @HostListener('document:keydown.enter')
    public onEnterKeyPress(): void {
        this.onSave();
    }

    @HostListener('document:keydown.escape', ['$event'])
    public onEscapeKeyPress(event: KeyboardEvent): void {
        event.stopPropagation();
        this.onReset();
    }
}
